'use strict'
import {
  Select,
  MenuItem,
  CircularProgress
} from '@material-ui/core'
import { DFToggle } from '../../df'

const LEGACY_FREQUENCY_OPTIONS = [
  { value: '', label: 'Unspecified' },
  { value: 'Daily', label: 'Daily' },
  { value: 'Every Other Day (M-W-F)', label: 'Every Other Day (M-W-F)' },
  { value: 'Twice a Week', label: 'Twice a Week' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Bi-Weekly', label: 'Bi-Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Semi-Annually', label: 'Semi-Annually' },
  { value: 'Annually', label: 'Annually' },
  { value: 'On Demand', label: 'On Demand' }
]

const DAY_OPTIONS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']

const WEEK_TYPE_OPTIONS = [
  { value: '', label: 'Any week' },
  { value: 'A', label: 'Week A' },
  { value: 'B', label: 'Week B' }
]

const sortDays = (days = []) => {
  return [...new Set(days.filter(Boolean))]
    .sort((a, b) => DAY_OPTIONS.indexOf(a) - DAY_OPTIONS.indexOf(b))
}

/* ── Inline SVG icons ───────────────────────────────────────────── */
const LightningSvg = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <path d="M13 2L3 14h9l-1 10 10-12h-9l1-10z" />
  </svg>
)
const SyncSvg = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
)
const CollapseSvg = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="18 15 12 9 6 15" />
  </svg>
)

/* ── Shared micro-label ─────────────────────────────────────────── */
const FieldLabel = ({ children }) => (
  <div style={{
    fontSize: 10, fontWeight: 700, color: 'var(--df-ink-4)',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4
  }}>{children}</div>
)

/* ── Small DF-styled button ─────────────────────────────────────── */
const DFBtn = ({ variant = 'ghost', disabled, onClick, children }) => {
  const isPrimary = variant === 'primary'
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
        fontFamily: 'inherit', cursor: disabled ? 'not-allowed' : 'pointer',
        border: isPrimary ? 'none' : '1px solid var(--df-line-2)',
        background: isPrimary ? 'var(--df-primary)' : 'var(--df-surface)',
        color: isPrimary ? '#fff' : 'var(--df-primary)',
        opacity: disabled ? 0.5 : 1,
        transition: 'background .12s, opacity .12s',
      }}
    >{children}</button>
  )
}

export default function PropertyRowDetail ({
  row,
  crews,
  routes,
  isEditable,
  isLiveMode,
  isDirty,
  isSaving,
  isMoving,
  isSuggesting,
  isResyncing,
  moveTarget,
  onUpdateField,
  onUpdateFrequency,
  onMoveTargetChange,
  onMove,
  onSuggest,
  onSave,
  onResync,
  onCollapse
}) {
  const currentAssignment = row.assignedRouteId || row.assignedCrewId
  const canMove = isEditable && !isLiveMode && moveTarget && moveTarget !== currentAssignment && !isMoving
  const moveOptions = (routes && routes.length > 0) ? routes : crews

  return (
    <div className="prop-detail">
      {/* ── Frequency + schedule row ────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginBottom: 14 }}>
        <div>
          <FieldLabel>Frequency</FieldLabel>
          <Select
            value={row.frequency || ''}
            onChange={(e) => onUpdateFrequency(row.aspirePropertyId, e.target.value)}
            variant="outlined"
            size="small"
            style={{ minWidth: 180, fontSize: 12 }}
            disabled={!isEditable}
          >
            {LEGACY_FREQUENCY_OPTIONS.map(opt => (
              <MenuItem key={opt.value || 'unspecified'} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </div>

        {(row.frequency === 'Weekly' || row.frequency === 'Bi-Weekly') && (
          <div>
            <FieldLabel>Day</FieldLabel>
            <Select
              value={row.preferredDay || ''}
              onChange={(e) => onUpdateField(row.aspirePropertyId, 'preferredDay', e.target.value)}
              variant="outlined"
              size="small"
              style={{ minWidth: 140, fontSize: 12 }}
              disabled={!isEditable}
            >
              <MenuItem value="">No day</MenuItem>
              {DAY_OPTIONS.map(day => (
                <MenuItem key={day} value={day}>{day}</MenuItem>
              ))}
            </Select>
          </div>
        )}

        {row.frequency === 'Twice a Week' && (
          <div>
            <FieldLabel>Days</FieldLabel>
            <Select
              multiple
              value={sortDays(row.preferredDays || [])}
              onChange={(e) => {
                const incoming = Array.isArray(e.target.value) ? e.target.value : String(e.target.value || '').split(',')
                const next = sortDays(incoming).slice(0, 2)
                onUpdateField(row.aspirePropertyId, 'preferredDays', next)
                if (!row.preferredDay && next[0]) {
                  onUpdateField(row.aspirePropertyId, 'preferredDay', next[0])
                }
              }}
              variant="outlined"
              size="small"
              style={{ minWidth: 180, fontSize: 12 }}
              disabled={!isEditable}
              renderValue={(selected) => (selected || []).join(', ') || 'No days'}
            >
              {DAY_OPTIONS.map(day => (
                <MenuItem key={day} value={day}>{day}</MenuItem>
              ))}
            </Select>
          </div>
        )}

        {row.frequency === 'Bi-Weekly' && (
          <div>
            <FieldLabel>Week Type</FieldLabel>
            <Select
              value={row.weekType || ''}
              onChange={(e) => onUpdateField(row.aspirePropertyId, 'weekType', e.target.value)}
              variant="outlined"
              size="small"
              style={{ minWidth: 120, fontSize: 12 }}
              disabled={!isEditable}
            >
              {WEEK_TYPE_OPTIONS.map(opt => (
                <MenuItem key={opt.value || 'none'} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FieldLabel>VIP</FieldLabel>
            <DFToggle
              checked={!!row.isVip}
              onChange={(val) => onUpdateField(row.aspirePropertyId, 'isVip', val)}
              disabled={!isEditable}
            />
          </div>
        </div>
      </div>

      {/* ── Lock + Move row ─────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, marginBottom: 14 }}>
        <div>
          <FieldLabel>Lock to route</FieldLabel>
          <Select
            value={row.lockedRouteId || row.lockedCrewId || ''}
            onChange={(e) => {
              const val = e.target.value
              const isRoute = (routes || []).some(r => r._id === val)
              if (isRoute) {
                onUpdateField(row.aspirePropertyId, 'lockedRouteId', val)
                const route = (routes || []).find(r => r._id === val)
                if (route?.crewId) onUpdateField(row.aspirePropertyId, 'lockedCrewId', route.crewId)
              } else {
                onUpdateField(row.aspirePropertyId, 'lockedRouteId', '')
                onUpdateField(row.aspirePropertyId, 'lockedCrewId', val)
              }
            }}
            variant="outlined"
            size="small"
            style={{ minWidth: 180, fontSize: 12 }}
            disabled={!isEditable}
          >
            <MenuItem value="">Unlocked</MenuItem>
            {(routes && routes.length > 0 ? routes : crews).map(item => (
              <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>
            ))}
          </Select>
        </div>

        <div>
          <FieldLabel>Move to route</FieldLabel>
          <Select
            value={moveTarget || ''}
            onChange={(e) => onMoveTargetChange(row.aspirePropertyId, e.target.value)}
            variant="outlined"
            size="small"
            style={{ minWidth: 180, fontSize: 12 }}
            disabled={!isEditable || isLiveMode || isMoving}
          >
            <MenuItem value="" disabled>{isLiveMode ? 'Scenario required' : 'Select route'}</MenuItem>
            {moveOptions.map(item => (
              <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>
            ))}
          </Select>
        </div>

        <DFBtn variant="ghost" disabled={!canMove} onClick={() => onMove(row)}>
          {isLiveMode ? 'Scenario required' : isMoving ? 'Moving…' : 'Move'}
        </DFBtn>
        <DFBtn variant="ghost" disabled={!isEditable || isLiveMode || isSuggesting} onClick={() => onSuggest(row)}>
          <LightningSvg />
          {isSuggesting ? 'Suggesting…' : 'Suggest Route'}
        </DFBtn>
      </div>

      {/* ── Resync row ──────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <span style={{ fontSize: 11, color: 'var(--df-ink-4)' }}>
          Last synced: {row.lastSyncedAt ? new Date(row.lastSyncedAt).toLocaleString() : 'Never'}
        </span>
        <DFBtn variant="ghost" disabled={isResyncing} onClick={() => onResync(row)}>
          {isResyncing ? <CircularProgress size={12} style={{ color: 'var(--df-primary)' }} /> : <SyncSvg />}
          {isResyncing ? 'Syncing…' : 'Resync from Aspire'}
        </DFBtn>
      </div>

      {/* ── Actions ─────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', justifyContent: 'flex-end', gap: 8,
        paddingTop: 12, borderTop: '1px solid var(--df-line)'
      }}>
        <DFBtn variant="ghost" onClick={onCollapse}>
          <CollapseSvg /> Collapse
        </DFBtn>
        <DFBtn
          variant="primary"
          disabled={!isEditable || !isDirty || isSaving}
          onClick={() => onSave(row)}
        >
          {isSaving
            ? <><CircularProgress size={12} style={{ color: '#fff' }} /> Saving…</>
            : isLiveMode ? 'Save to Live' : 'Save Changes'}
        </DFBtn>
      </div>
    </div>
  )
}
